import type { RowData } from "@tanstack/react-table";
import { resolveColumnMeta } from "../columns/columnKinds";
import type { DataGridColumnInstance } from "../columns/types";
import type { GridController } from "../focus/gridController";
import { getDisplayColumns } from "../focus/navigation";
import { isFillColumn } from "../layout/fillColumns";

interface CommitResizeWidthsInput<TData extends RowData> {
    column: DataGridColumnInstance<TData>;
    controller: GridController;
    edge: "start" | "end";
}

/**
 * Runs before a column resizes. Commits the column's rendered width so the
 * resize starts where the edge is drawn. On an end-edge resize it also
 * commits fill columns before it, which would otherwise absorb the change and
 * move the column's start edge. Fill columns after it keep filling, so they
 * give or take the width. Returns a release for when the resize ends: it
 * drops the widths held for `fillUntilResized` columns, so they fill again.
 */
export const commitResizeWidths = <TData extends RowData>({
    column,
    controller,
    edge,
}: CommitResizeWidthsInput<TData>): (() => void) => {
    const table = column.table;
    const sizing = table.atoms.columnSizing.get();
    const renderedWidths = controller.layout.widths;
    const displayColumns = getDisplayColumns(table);
    const index = displayColumns.findIndex(
        (candidate) => candidate.id === column.id,
    );
    const leadingFillColumns =
        edge === "end" && index > 0
            ? displayColumns
                  .slice(0, index)
                  .filter((candidate) =>
                      isFillColumn(
                          resolveColumnMeta(candidate.columnDef, candidate.id),
                      ),
                  )
            : [];
    const committedEntries = [column, ...leadingFillColumns].flatMap(
        (candidate) => {
            const width = renderedWidths[candidate.id];
            return width === undefined || candidate.id in sizing
                ? []
                : [[candidate.id, width] satisfies [string, number]];
        },
    );
    if (committedEntries.length > 0) {
        table.setColumnSizing((old) => ({
            ...old,
            ...Object.fromEntries(committedEntries),
        }));
    }
    const heldIds = new Set(
        leadingFillColumns
            .filter(
                (candidate) =>
                    resolveColumnMeta(candidate.columnDef, candidate.id)
                        .fillUntilResized,
            )
            .map((candidate) => candidate.id)
            .filter((id) =>
                committedEntries.some(([committedId]) => committedId === id),
            ),
    );
    return () => {
        if (heldIds.size === 0) {
            return;
        }
        table.setColumnSizing((old) =>
            Object.fromEntries(
                Object.entries(old).filter(([id]) => !heldIds.has(id)),
            ),
        );
    };
};
